import { ConflictErrorCode, GoneErrorCode, NotFoundErrorCode, ValidationErrorCode } from '@today/types'
import { isAxiosError } from 'axios'

/**
 * A compliance API refusal, mapped for display (TO-232 compliance-admin-ui REQ-206).
 *
 * @property message - The form-level message, or null when the refusal is shown per field.
 * @property fields - Field name → message, from a `VALIDATION_ERROR`'s `details.fields`.
 * @property candidates - The customer ids of a `SUBJECT_AMBIGUOUS` refusal.
 */
export interface ComplianceErrorView {
	message: string | null
	fields: Record<string, string>
	candidates: string[]
}

/** The codes with a REQ-206 message, keyed `errors.<code>` in the compliance catalog. */
const MAPPED_CODES: ReadonlySet<string> = new Set([
	NotFoundErrorCode.SUBJECT_NOT_FOUND,
	ConflictErrorCode.SUBJECT_ALREADY_ERASED,
	ConflictErrorCode.LEGAL_HOLD_ACTIVE,
	ConflictErrorCode.ERASURE_ALREADY_OPEN,
	ConflictErrorCode.ERASURE_INVALID_STATE,
	ConflictErrorCode.LEGAL_HOLD_ALREADY_RELEASED,
	ConflictErrorCode.SAR_NOT_READY,
	GoneErrorCode.SAR_EXPIRED,
	ConflictErrorCode.SUBJECT_AMBIGUOUS,
])

interface ApiErrorBody {
	code?: unknown
	details?: { fields?: unknown; customerIds?: unknown }
}

const fieldsOf = (fields: unknown): Record<string, string> => {
	if (!Array.isArray(fields)) return {}
	const out: Record<string, string> = {}
	for (const entry of fields as unknown[]) {
		const { field, message } = (entry ?? {}) as { field?: unknown; message?: unknown }
		if (typeof field === 'string' && typeof message === 'string') out[field] = message
	}
	return out
}

const candidatesOf = (ids: unknown): string[] =>
	Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []

/** Maps any failure to its REQ-206 message; an unknown code or a network failure gets the generic one. */
export const complianceError = (error: unknown, t: (key: string) => string): ComplianceErrorView => {
	const body = isAxiosError<{ error?: ApiErrorBody }>(error) ? error.response?.data?.error : undefined
	const code = typeof body?.code === 'string' ? body.code : null
	if (code === ValidationErrorCode.VALIDATION_ERROR) {
		const fields = fieldsOf(body?.details?.fields)
		if (Object.keys(fields).length > 0) return { message: null, fields, candidates: [] }
	}
	return {
		message: t(code && MAPPED_CODES.has(code) ? `errors.${code}` : 'errors.generic'),
		fields: {},
		candidates: code === ConflictErrorCode.SUBJECT_AMBIGUOUS ? candidatesOf(body?.details?.customerIds) : [],
	}
}
